import { Component, EventEmitter, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { NgxSpinnerService } from 'ngx-spinner';
import { ConfirmationService } from 'primeng/api';
import { AuthConfig, AuthService, ResponseObject } from 'src/app/Services/auth.service';
import { GalleriesService, GalleryAddress } from 'src/app/Services/galleries.service';
import { Cities, Countries, RegistrationsService, States } from 'src/app/Services/registration.service';
import { AddressSuggestion } from 'src/app/Services/mapbox-address.service';
import { NotificationsService } from 'src/app/Services/shared/notifications.service';
import { NotificationTypes } from 'src/app/app.enums';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'app-view-gallery-address',
    templateUrl: './view-gallery-address.component.html',
    inputs: ['galleryAddresses', 'galleryId'],
    outputs: ['onAddressChanged'],
    standalone: false
})
export class ViewGalleryAddressComponent implements OnInit {
  public galleryAddresses: GalleryAddress[] = [];
  public onAddressChanged = new EventEmitter<GalleryAddress[]>();
  public addressTypes: string[] = [];
  public authConfig: AuthConfig = {};
  public visible: boolean = false;
  public selectedGalleryAddress!: GalleryAddress;
  public isAddressTypeDisabled: boolean = false;
  public isEditMode: boolean = false;
  public galleryId!: string;

  public selectedCountry!: string;
  public selectedState!: string;
  public selectedCity!: string;
  public countries: Countries[] = [];
  public states: States[] = [];
  public cities: Cities[] = [];

  constructor(
    private router: Router,
    private jwtHelper: JwtHelperService,
    private ngxSpinnerService: NgxSpinnerService,
    private notificationsService: NotificationsService,
    private galleriesService: GalleriesService,
    private registrationsService: RegistrationsService,
    private confirmationService: ConfirmationService,
    private authService: AuthService,
    private transloco: TranslocoService
  ) {
    this.selectedGalleryAddress = new GalleryAddress();
  }
public ngOnInit(): void {
  this.authConfig = this.authService.getAuthConfig();
  this.fetchCountries(); // Fetch countries on initialization

  // If a country is already selected, fetch its states and cities
  if (this.selectedGalleryAddress.Country) {
    const selectedCountry = this.countries.find((c) => c.name === this.selectedGalleryAddress.Country);
    if (selectedCountry) {
      this.fetchStates(selectedCountry.id);
    }
  }

  if (this.selectedGalleryAddress.State) {
    const selectedState = this.states.find((s) => s.name === this.selectedGalleryAddress.State);
    if (selectedState) {
      this.fetchCities(selectedState.id);
    }
  }
}

  public showAddressDialog(): void {
    this.selectedGalleryAddress = new GalleryAddress();
    this.resetAddressTypesOptions();
    this.fetchCountries();
    this.isEditMode = false;
    this.visible = true;
  }

  private resetAddressTypesOptions(): void {
    const addressTypes = ['StreetAddress', 'PostalAddress', 'DeliveredAddress'];
    const storedAddressTypes = this.galleryAddresses.map((p) => p.AddressType);
    storedAddressTypes.forEach((p) => {
      if (p && addressTypes.indexOf(p) >= 0) {
        addressTypes.splice(addressTypes.indexOf(p), 1);
      }
    });
    this.addressTypes = addressTypes;
  }

public saveAddress(event: Event): void {
  event.preventDefault(); // Prevent default form submission behavior

  const existingAddressIndex = this.galleryAddresses.findIndex(
    (address) => address.Id === this.selectedGalleryAddress.Id
  );

  if (existingAddressIndex !== -1) {
    this.galleryAddresses[existingAddressIndex] = { ...this.selectedGalleryAddress };
  } else {
    this.galleryAddresses.push({ ...this.selectedGalleryAddress });
  }

  this.ngxSpinnerService.show();
  this.galleriesService.saveGalleryAddress(this.galleryId, this.selectedGalleryAddress).subscribe(
    (response: ResponseObject) => {
      this.ngxSpinnerService.hide();
      if (response.Success) {
        this.onAddressChanged.emit([...this.galleryAddresses]);
        this.selectedGalleryAddress = new GalleryAddress();
        this.visible = false;
        this.notificationsService.showNotification(this.transloco.translate('viewGalleryAddress.successTitle'), response.ResponseMessage, NotificationTypes.Success);
      } else {
        this.notificationsService.showNotification(this.transloco.translate('viewGalleryAddress.errorTitle'), response.ResponseMessage, NotificationTypes.Error);
      }
    },
    (error) => {
      console.error(error);
      this.ngxSpinnerService.hide();
    }
  );
}

  public deleteAddress(address: GalleryAddress): void {
    this.confirmationService.confirm({
      message: this.transloco.translate('viewGalleryAddress.deleteAddressConfirm'),
      accept: () => {
        this.ngxSpinnerService.show();
        this.galleriesService.deleteAddress(address.Id).subscribe(
          (response: ResponseObject) => {
            this.ngxSpinnerService.hide();
            if (response.Success) {
              this.galleryAddresses = this.galleryAddresses.filter((item) => item.Id !== address.Id);
              this.onAddressChanged.emit([...this.galleryAddresses]);
              this.notificationsService.showNotification(this.transloco.translate('viewGalleryAddress.successTitle'), response.ResponseMessage, NotificationTypes.Success);
            } else {
              this.notificationsService.showNotification(this.transloco.translate('viewGalleryAddress.errorTitle'), response.ResponseMessage, NotificationTypes.Error);
            }
          },
          (error) => {
            console.error(error);
            this.ngxSpinnerService.hide();
          }
        );
      }
    });
  }

  public closeDialog(): void {
    this.selectedCountry = '';
    this.selectedState = '';
    this.selectedCity = '';
    this.selectedGalleryAddress = new GalleryAddress();
    this.isAddressTypeDisabled = false;
    this.visible = false;
  }

  public editAddress(selectedAddress: GalleryAddress): void {
    this.fetchCountries();
    this.selectedGalleryAddress = { ...selectedAddress };
    this.isEditMode = true;
    this.resetAddressTypesOptions();
    this.isAddressTypeDisabled = true;
    this.visible = true;
  }


 

private fetchCountries(): void {
  this.registrationsService.getAllCountries().subscribe(
    (countries) => {
      this.countries = countries;

      // Automatically fetch states if a country is already selected
      const selectedCountry = this.countries.find((c) => c.name === this.selectedGalleryAddress.Country);
      if (selectedCountry) {
        this.selectedCountry = selectedCountry.name;
        this.fetchStates(selectedCountry.id);
      }
    },
    (error) => {
      console.error('Error fetching countries:', error);
    }
  );
}

public onCountryChange(event: any): void {
  const selectedCountryName = event.target.value;
  this.selectedCountry = selectedCountryName;
  this.selectedGalleryAddress.Country = selectedCountryName;

  // Reset state and city selections
  this.selectedState = '';
  this.selectedCity = '';
  this.states = [];
  this.cities = [];

  // Fetch states for the selected country
  const selectedCountry = this.countries.find((c) => c.name === selectedCountryName);
  if (selectedCountry) {
    this.fetchStates(selectedCountry.id);
  } else {
    console.error('Selected country not found');
  }
}


public onStateChange(event: any): void {
  const selectedStateName = event.target.value;
  this.selectedState = selectedStateName;
  this.selectedGalleryAddress.State = selectedStateName;
  // Reset city selection
  this.selectedCity = '';
  this.cities = [];

  // Fetch cities for the selected state
  const selectedState = this.states.find((s) => s.name === selectedStateName);
  if (selectedState) {
    this.fetchCities(selectedState.id);
  } else {
    console.error('Selected state not found');
  }
}



  public onCityChange(event: any): void {
    const selectedCityName = event.target.value;
    this.selectedCity = selectedCityName;
    this.selectedGalleryAddress.City = selectedCityName;
  }

  /**
   * Fill the gallery address from a Mapbox suggestion. Note the adapter: the suburb maps
   * to the gallery model's SuburbTown field (vs the artist model's Suburb) — unifying the
   * two address shapes behind one component (requirement E). Only sets provided fields.
   */
  public onAddressSelected(a: AddressSuggestion): void {
    if (a.country) this.selectedGalleryAddress.Country = a.country;
    if (a.state) this.selectedGalleryAddress.State = a.state;
    if (a.city) this.selectedGalleryAddress.City = a.city;
    if (a.suburb) this.selectedGalleryAddress.SuburbTown = a.suburb;
    if (a.postalCode) this.selectedGalleryAddress.PostalCode = a.postalCode;
  }


private fetchStates(countryId: number): void {
  this.registrationsService.getAllStates(countryId).subscribe(
    (states) => {
      this.states = states;

      // Automatically fetch cities if a state is already selected
      const selectedState = this.states.find((s) => s.name === this.selectedGalleryAddress.State);
      if (selectedState) {
        this.selectedState = selectedState.name;
        this.fetchCities(selectedState.id);
      }
    },
    (error) => {
      console.error('Error fetching states:', error);
    }
  );
}


private fetchCities(stateId: number): void {
  this.registrationsService.getAllCities(stateId).subscribe(
    (cities) => {
      this.cities = cities;

      // Automatically set the selected city if it exists
      const selectedCity = this.cities.find((c) => c.name === this.selectedGalleryAddress.City);
      if (selectedCity) {
        this.selectedCity = selectedCity.name;
      }
    },
    (error) => {
      console.error('Error fetching cities:', error);
    }
  );
}
}